<template>
  <div v-if="globalUser">
    <div class="mt-1" v-if="isLoadingOrgs">
      <loading-spinner/>
    </div>
    <div v-else-if="orgs && orgs.length">
      {{ $t("Organization") }}
      <div class="mt-1 org-switch orange-switch">
        <select
            ref="orgswitch"
            v-model="selected"
            :disabled="orgSelectionDisabled"
            class="custom-select">
          <option
              v-for="(org, index) in orgs"
              :key="index"
              v-if="org.hasPerm || $store.getters.isSuperAdmin"
              :value="org.id">
            {{ org.displayName ? org.displayName : org.name }}
          </option>
        </select>
      </div>
    </div>

    <div class="mt-2" v-if="isLoadingTeams">
      <loading-spinner/>
    </div>
    <div v-else-if="teams && teams.length && showTeams" class="mt-2">
      {{ $t("Team") }}
      <div class="org-switch orange-switch">
        <select ref="teamswitch" v-model="selectedTeam" class="custom-select">
          <option
              v-for="(team, index) in teams"
              v-if="team.organizationId === selected && (team.hasPerm || $store.getters.isSuperAdmin)"
              :key="index"
              :value="team.id"
          >
            {{ team.displayName ? team.displayName : team.name }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<script>
import {AdminData} from "@/enum";
import {mapState} from "vuex";
import ArrayMixin from "@/components/mixins/ArrayMixin.js";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

export default {
  name: "OrganizationSwitcher",
  components: {LoadingSpinner},
  mixins: [ArrayMixin],
  props: {
    excludeViewers: {type: Boolean, default: true},
    op: {type: String, default: "create"},
    opOnly: {type: Boolean, default: false},
    showTeams: {type: Boolean, default: true},
    loadTeamsButDontShow: {type: Boolean, default: false},
    showAllOrgs: {type: Boolean, default: false},
    preSetOrganizationId: {type: String, default: null},
    orgSelectionDisabled: {type: Boolean, default: false},
    excludeOrganizations: {type: Array, default: () => [],},
  },
  data() {
    return {
      orgs: [],
      permissionedOrgs: [],
      permissionedTeams: [],
      teams: [],
      AdminData: AdminData,
      userId: "",
      selected: "",
      selectedTeam: "",
      isLoadingOrgs: false,
      isLoadingTeams: false,
    };
  },
  computed: mapState({
    globalUser: (state) => state.users.globalUser,
    globalTeams: (state) => state.dynamicStore.globalTeams,
    globalOrganizations: (state) => state.organization.globalOrganizations,
  }),
  watch: {
    globalTeams() {
      if (this.$store.getters.isSuperAdmin) {
        const tempTeams = this.globalTeams;
        this.teams = Object.keys(tempTeams).map((id) => {
          return tempTeams[id];
        });
      }
    },
    globalUser(user) {
      if (user) {
        this.init();
      }
    },
    /**
     * @callback setTeam - emits the selected team id
     * @callback unsetTeam - no params
     * */
    selectedTeam() {
      if (this.selectedTeam === "none") {
        this.$emit("unsetTeam");
      } else {
        this.$emit("setTeam", this.selectedTeam);
      }
    },
    /**
     * @callback setOrganization - emits the selected organization id
     * */
    selected() {
      this.$emit("setOrganization", this.selected);
      const org = this.permissionedOrgs.filter((item) => {
        return item.id === this.selected;
      });
      let publicTeams = this.getPublicTeamsForOrg(this.selected);

      if (!publicTeams) {
        publicTeams = this.getAnyTeamForOrg(this.selected);
      }
      if (org[0]) {
        this.teams.unshift({
          id: "none",
          displayName: "none",
          organizationId: this.selected,
          hasPerm: true,
        });
        this.selectedTeam = publicTeams && publicTeams.length ? publicTeams[0].id : null;
      } else {
        this.teams = this.teams.filter((team) => {
          return team.id !== "none";
        });
        this.selectedTeam = publicTeams && publicTeams.length ? publicTeams[0].id : null;
      }
    },
  },
  beforeMount() {
    this.init();
  },
  methods: {
    getAnyTeamForOrg(orgId) {
      return this.teams.find((team) => {
        return team.organizationId === orgId;
      });
    },
    getPublicTeamsForOrg(orgId) {
      return this.teams.filter((team) => {
        return team.organizationId === orgId && team.visibility === 2;
      });
    },
    async init() {
      await this.loadOrganizations();
      await this.loadTeams();
      this.removeOrgsWithoutPermsAndWithoutTeams();
      this.setInitialActive();
    },
    removeOrgsWithoutPermsAndWithoutTeams() {
      if(this.showAllOrgs || this.$store.getters.isSuperAdmin) {
        return;
      }

      this.orgs = this.orgs.filter((org) => {
        if(org.hasPerm) {
          return true;
        }
        const teamsForOrg = this.teams.filter((team) => {
          return team.organizationId === org.id && team.hasPerm;
        });
        org.hasPerm = teamsForOrg && teamsForOrg.length > 0;
        return org.hasPerm;
      });
    },
    getOrgs(orgs) {
      const tempOrgs = this.globalOrganizations;
      const orgKeys = Object.keys(tempOrgs);
      orgs = orgKeys.map((id) => {
        return tempOrgs[id];
      });
      return orgs.sort(this.objectComparer("displayName"));
    },
    filterOrgs(orgs) {
     return orgs.filter((org) => {
        return !this.$store.getters.isSuperOrg(org.id) && !this.excludeOrganizations.includes(org.id);
      });
    },
    filterPrivateOrgs(orgs) {
      return orgs.filter((org) => {
        return org.visibility > 0;
      });
    },
    addPermsToOrgs(orgs) {
      this.orgs = orgs.map((org) => {
        if(this.$store.getters.isSuperAdmin) {
          org.hasPerm = true;
          return org;
        }
        if(this.permissionedOrgs && this.permissionedOrgs.find((o => o.id === org.id))) {
          org.hasPerm = true;
        } else {
          org.hasPerm = false;
        }
        return org;
      });
    },
    addPermsToTeams(teams) {
      this.teams = teams.map((team) => {
        if(this.$store.getters.isSuperAdmin) {
          team.hasPerm = true;
          return team;
        }
        const teamOrg = this.permissionedOrgs.find((o) => o.id === team.organizationId);
        if(teamOrg) {
          team.hasPerm = true;
          return team;
        }
        if(this.permissionedTeams && this.permissionedTeams.find((t => t.id === team.id))) {
          team.hasPerm = true;
        } else {
          team.hasPerm = false;
        }
        return team;
      });
    },
    async loadPermissionedOrgs() {
      return await this.$store.dispatch("getUserOrganizationsWithPermission", {
        op: this.op,
      });
    },
    async loadOrganizations() {
      console.log('loading organizations for op:', this.op);
      this.isLoadingOrgs = true;
      let orgs = [];
      if (this.$store.getters.isSuperAdmin) {
        orgs = this.getOrgs();
        orgs = this.filterPrivateOrgs(orgs);
      } else if (this.showAllOrgs) {
        orgs = this.getOrgs();
        orgs = this.filterOrgs(orgs);
        // need to set perms as false because else they get saved as true
        orgs = orgs.map((org) => {
          org.hasPerm = false;
          return org;
        });
        return;
      } else {
        orgs = this.getOrgs();
        this.permissionedOrgs = await this.loadPermissionedOrgs();
        orgs = this.$store.getters.getUserOrganizations();
      }
      this.addPermsToOrgs(orgs);
      this.isLoadingOrgs = false;
      console.log('loaded organizations:', this.orgs);

      return this.orgs.sort(this.objectComparer("displayName"));
    },
    async loadTeams() {
      let teams = [];
      if (this.showTeams || this.loadTeamsButDontShow) {
        this.isLoadingTeams = true;
        if (this.$store.getters.isSuperAdmin) {
          teams = await this.$store.dispatch("loadTeams", {listName: 'globalTeams'});
        } else {
          console.log('loading teams with permission for op:', this.op);
          teams = await this.$store.dispatch("loadTeams", {listName: 'globalTeams'});
          this.permissionedTeams = await this.$store.dispatch("getUserTeamsWithPermission", {op: this.op})
          console.log(this.teams);
        }
        this.isLoadingTeams = false;
      }
      this.addPermsToTeams(teams);
      return this.teams && this.teams.length > 1 ? this.teams.sort(this.objectComparer("displayName")) : [];
    },
    setInitialActive() {
      if (this.orgs && this.preSetOrganizationId) {
        this.selected = this.preSetOrganizationId;
      } else if (this.orgs && this.orgs.length === 1) {
        // if only one organization is available, set it to active
        this.selected = this.orgs[0].id;
      }
    },
  },
};
</script>

<style lang="scss">
#sfx-user-navigation {
  .org-switch {
    background-color: rgba(0, 0, 0, 0.3);
    display: inline-block;
    margin-bottom: 0;
  }
}
</style>
